import { ApiService } from '@supply-mover-2-0/core';
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
	type ReactNode,
} from 'react';
import { AiAgentOverlayManager } from '../../ai-agent/services/AiAgentOverlayManager';
import { useAgentChatData } from '../../providers/AgentChatDataProvider';

export interface WidgetAgent {
	id: number;
	name: string;
	faIcon?: string | null;
	agentColor?: string | null;
	agentType: string;
	enableVoice?: boolean;
	/** For system agents — passed to the gateway instead of agentId */
	systemAgentType?: string;
}

export interface WidgetSession {
	id: number;
	createdDate: string;
	endDate?: string | null;
	status: string;
	sessionType: string;
	agentName?: string;
	agentFaIcon?: string | null;
	agentColor?: string | null;
}

type WidgetView = 'home' | 'active-chat' | 'history-detail';

interface AgentWidgetState {
	isOpen: boolean;
	view: WidgetView;
	defaultAgent: WidgetAgent | null;
	sessions: Array<WidgetSession>;
	sessionsLoading: boolean;
	activeSessionId: string | null;
	selectedHistorySession: number | null;
	selectedMode: 'chat' | 'voice' | null;
	loading: boolean;
	error: string | null;
}

interface AgentWidgetContextValue extends AgentWidgetState {
	toggleWidget: () => void;
	openWidget: () => void;
	closeWidget: () => void;
	setView: (view: WidgetView) => void;
	viewHistorySession: (sessionId: number) => void;
	selectMode: (mode: 'chat' | 'voice') => void;
	goHome: () => void;
	refreshSessions: () => Promise<void>;
}

const AgentChatWidgetContext = createContext<AgentWidgetContextValue | null>(
	null,
);

export const useAgentWidget = (): AgentWidgetContextValue => {
	const context = useContext(AgentChatWidgetContext);

	if (context == null) {
		throw new Error(
			'useAgentWidget must be used within an AgentChatWidgetProvider',
		);
	}

	return context;
};

interface AgentChatWidgetProviderProps {
	children: ReactNode;
}

export const AgentChatWidgetProvider: React.FC<
	AgentChatWidgetProviderProps
> = ({ children }) => {
	// Preloaded data from the data provider — when `enabled` is false, the
	// tenant has the system agent disabled and the widget hides itself.
	const preloadedData = useAgentChatData();

	const [state, setState] = useState<AgentWidgetState>(() => ({
		isOpen: false,
		view: 'home',
		defaultAgent: null,
		sessions: [],
		sessionsLoading: false,
		activeSessionId: null,
		selectedHistorySession: null,
		selectedMode: null,
		loading: preloadedData?.systemAgentData == null,
		error: null,
	}));

	// Sync the resolved system agent into widget state once preload completes.
	useEffect(() => {
		if (preloadedData?.systemAgentData == null) {
			return;
		}

		const data = preloadedData.systemAgentData;

		if (data.enabled && data.agent != null) {
			setState((prev) => ({
				...prev,
				defaultAgent: {
					id: data.agent!.id,
					name: data.agent!.name,
					faIcon: data.agent!.faIcon,
					agentColor: data.agent!.agentColor,
					agentType: 'system',
					enableVoice: data.agent!.enableVoice,
					systemAgentType: data.agent!.systemAgentType,
				},
				loading: false,
			}));
		} else {
			setState((prev) => ({
				...prev,
				defaultAgent: null,
				loading: false,
			}));
		}
	}, [preloadedData?.systemAgentData]);

	const fetchSessions = useCallback(async () => {
		const systemAgentType = state.defaultAgent?.systemAgentType;

		if (systemAgentType == null) {
			return;
		}

		setState((prev) => ({ ...prev, sessionsLoading: true }));

		try {
			const response = await ApiService.apiGet<{
				success: boolean;
				data?: Array<WidgetSession>;
			}>(
				`/ai-agents/chat-widget/my-sessions?limit=10&systemAgentType=${encodeURIComponent(systemAgentType)}`,
			);

			if (response.success && Array.isArray(response.data)) {
				setState((prev) => ({
					...prev,
					sessions: response.data ?? [],
					sessionsLoading: false,
				}));
			} else {
				setState((prev) => ({
					...prev,
					sessions: [],
					sessionsLoading: false,
				}));
			}
		} catch (error) {
			console.debug('Failed to fetch sessions:', error);
			setState((prev) => ({
				...prev,
				sessions: [],
				sessionsLoading: false,
			}));
		}
	}, [state.defaultAgent?.systemAgentType]);

	useEffect(() => {
		if (state.isOpen && !state.sessionsLoading && state.defaultAgent != null) {
			void fetchSessions();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.isOpen]);

	const toggleWidget = useCallback(() => {
		setState((prev) => ({
			...prev,
			isOpen: !prev.isOpen,
			view: prev.isOpen ? 'home' : prev.view,
		}));
	}, []);

	const openWidget = useCallback(() => {
		setState((prev) => ({ ...prev, isOpen: true }));
	}, []);

	const closeWidget = useCallback(() => {
		setState((prev) => ({
			...prev,
			isOpen: false,
			view: 'home',
			selectedHistorySession: null,
		}));
	}, []);

	const setView = useCallback((view: WidgetView) => {
		setState((prev) => ({ ...prev, view }));
	}, []);

	const viewHistorySession = useCallback((sessionId: number) => {
		setState((prev) => ({
			...prev,
			selectedHistorySession: sessionId,
			view: 'history-detail',
		}));
	}, []);

	const selectMode = useCallback(
		(mode: 'chat' | 'voice') => {
			if (state.defaultAgent == null) {
				return;
			}

			// Launch the overlay manager FIRST (outside of setState)
			// This avoids React warning about updating OverlayProvider while rendering AgentChatWidgetProvider
			AiAgentOverlayManager.getInstance().showAgent(state.defaultAgent.id, {
				initialMode: mode,
				viewMode: mode === 'voice' ? 'modern' : 'chat',
				disableVoice: mode === 'chat',
				disableChat: mode === 'voice',
				agentName: state.defaultAgent.name,
				systemAgentType: state.defaultAgent.systemAgentType,
			});

			// THEN close the widget panel
			setState((prev) => ({
				...prev,
				isOpen: false,
				view: 'home',
				selectedMode: null,
			}));
		},
		[state.defaultAgent],
	);

	const goHome = useCallback(() => {
		setState((prev) => ({
			...prev,
			view: 'home',
			selectedHistorySession: null,
			selectedMode: null,
			activeSessionId: null,
		}));
	}, []);

	const refreshSessions = useCallback(async () => {
		await fetchSessions();
	}, [fetchSessions]);

	const value: AgentWidgetContextValue = {
		...state,
		toggleWidget,
		openWidget,
		closeWidget,
		setView,
		viewHistorySession,
		selectMode,
		goHome,
		refreshSessions,
	};

	return (
		<AgentChatWidgetContext.Provider value={value}>
			{children}
		</AgentChatWidgetContext.Provider>
	);
};
